import * as React from 'react';

import classNames from 'classnames';

import type {ActivityParticipantAttendanceStatus} from 'nutshell-graphql-types';

import {colors} from '../colors/colors';
import {AccountIcon} from '../icon/svg/account-icon';
import {AccountParentCircleIcon} from '../icon/svg/account-parent-circle-icon';
import {AccountParentIcon} from '../icon/svg/account-parent-icon';
import {AccountsCircleIcon} from '../icon/svg/accounts-circle-icon';
import {AttendeeCircleIcon} from '../icon/svg/attendee-circle-icon';
import {AttendeeSquareIcon} from '../icon/svg/attendee-square-icon';
import {ChatBubbleQuestionRegularIcon} from '../icon/svg/chat-bubble-question-regular-icon';
import {ContactIcon} from '../icon/svg/contact-icon';
import {ContactsCircleIcon} from '../icon/svg/contacts-circle-icon';
import {PeopleRegularIcon} from '../icon/svg/people-regular-icon';

import {AvatarAttendanceBadge} from './avatar-attendance-badge';
import {LeadAvatar} from './lead-avatar';

import styles from './avatar.module.css';

export const DEFAULT_SIZE = 30;

type Props = {
    avatarUrl: string | null | undefined;
    circular: boolean;
    // border adds a 2 px solid border to the avatar.
    border?: boolean;
    initials?: string | null | undefined;
    size: number;
    type?: string;
    /** Leads should also provide their status, for the alt tag */
    status?: string;
    isDisabled?: boolean;
    isParent?: boolean;
    tooltipText?: string;
    color?: string;
    isOnline?: boolean | null | undefined;
    hasBorderRadius?: boolean;
    displayBlock?: boolean;
    attendanceStatus?: ActivityParticipantAttendanceStatus;
};

/**
 * A simple graphical representation of a company, person, lead, user or team.
 */
export class Avatar extends React.PureComponent<Props> {
    static defaultProps = {
        circular: true,
        size: DEFAULT_SIZE,
    };

    render() {
        // Guard against missing or empty url AND initials (we need at least one of them)
        const needsPlaceholder =
            !this.props.avatarUrl &&
            !this.props.initials &&
            ![
                'contacts',
                'accounts',
                'activityExternalAttendees',
                'unidentifiedChat',
                'teams',
            ].includes(this.props.type ?? ''); // contacts and accounts (including mcfxCompanies) have icons to fallback on

        if (needsPlaceholder) {
            // Provide a placeholder div, to prevent other elements from shifting around
            return <div style={{height: this.props.size, width: this.props.size}} />;
        }

        if (this.props.type === 'leads' || this.props.type === 'leadStatus') {
            return (
                <LeadAvatar
                    avatarUrl={this.props.avatarUrl}
                    size={this.props.size}
                    status={this.props.status}
                />
            );
        }

        let style: any = {
            width: `${this.props.size}px`,
            height: `${this.props.size}px`,
            lineHeight: `${this.props.size}px`,
            fontSize: Math.floor(this.props.size / 2 - this.props.size / 15),
        };

        // Don't set transparent background for types that have their own background styling (like unidentifiedChat)
        if (!this.props.initials && this.props.type !== 'unidentifiedChat') {
            style = Object.assign(style, {backgroundColor: 'transparent'});
        }
        if (this.props.circular)
            style = Object.assign(style, {borderRadius: `${this.props.size}px`});

        const typeClassName = this.props.type ? `type-${this.props.type}` : '';

        // @ts-expect-error
        const containerStyleNames = classNames(typeClassName ? styles[typeClassName] : '', {
            [styles.container]: !this.props.border,
            [styles['container--border']]: this.props.border,
            [styles['container--border-radius']]: this.props.hasBorderRadius,
            [styles['container--display-block']]: this.props.displayBlock,
        });

        return (
            <div style={style} data-tip={this.props.tooltipText} className={containerStyleNames}>
                {this.renderAvatarContents(style)}
                {typeof this.props.isOnline === 'boolean' && (
                    <div
                        className={classNames(styles['status-indicator'], {
                            [styles.online]: this.props.isOnline,
                            [styles.offline]: !this.props.isOnline,
                        })}
                    />
                )}
                {this.props.attendanceStatus && (
                    <div className={styles['attendance-badge-slot']}>
                        <AvatarAttendanceBadge attendanceStatus={this.props.attendanceStatus} />
                    </div>
                )}
            </div>
        );
    }

    // This sucks, but will get fixed once we have a more consistent set of icons, and leads have avatarUrls
    renderAvatarContents(style: any) {
        let initials, avatarImg, entityIcon;

        if (this.props.initials && this.props.type !== 'activityExternalAttendees') {
            initials = (
                <div
                    style={style}
                    className={
                        this.props.isDisabled ? styles['initials--disabled'] : styles.initials
                    }
                >
                    {this.props.initials}
                </div>
            );
        }

        if (this.props.avatarUrl && this.props.type !== 'activityExternalAttendees') {
            const params = 'width=100&height=100';
            // Url may or may not already have a query string
            const avatarUrl: string = this.props.avatarUrl;
            const url = avatarUrl.includes('?')
                ? `${avatarUrl}&${params}`
                : `${avatarUrl}?${params}`;
            avatarImg = <img style={style} alt='avatar' src={url} />;
        }

        // Users don't have SVG entity icons… yet
        if (
            [
                'contacts',
                'accounts',
                'activityExternalAttendees',
                'unidentifiedChat',
                'teams',
            ].includes(this.props.type ?? '') &&
            !initials
        ) {
            if (this.props.type === 'contacts') {
                entityIcon = this.props.circular ? (
                    <ContactsCircleIcon
                        size={this.props.size}
                        // @ts-expect-error
                        fill={this.props.color || colors.navyDk}
                    />
                ) : (
                    <ContactIcon size={this.props.size} />
                );
            } else if (this.props.type === 'accounts' && !this.props.isParent) {
                entityIcon = this.props.circular ? (
                    <AccountsCircleIcon
                        size={this.props.size}
                        // @ts-expect-error
                        color={this.props.color || colors.company}
                    />
                ) : (
                    // @ts-expect-error
                    <AccountIcon size={this.props.size} fill={this.props.color} />
                );
            } else if (this.props.type === 'activityExternalAttendees') {
                entityIcon = this.props.circular ? (
                    <AttendeeCircleIcon
                        size={this.props.size}
                        // @ts-expect-error
                        fill={this.props.color || colors.offWhiteDk}
                    />
                ) : (
                    <AttendeeSquareIcon
                        size={this.props.size}
                        // @ts-expect-error
                        fill={this.props.color || colors.offWhiteDk}
                    />
                );
            } else if (this.props.type === 'unidentifiedChat') {
                entityIcon = (
                    <ChatBubbleQuestionRegularIcon
                        size={this.props.size}
                        // @ts-expect-error
                        fill={colors.white}
                    />
                );
            } else if (this.props.type === 'teams') {
                entityIcon = (
                    <PeopleRegularIcon
                        size={this.props.size}
                        wrapWithDiv={false}
                        // @ts-expect-error
                        color={this.props.color || colors.grey}
                    />
                );
            } else {
                entityIcon = this.props.circular ? (
                    <AccountParentCircleIcon
                        size={this.props.size}
                        // @ts-expect-error
                        color={this.props.color || colors.company}
                    />
                ) : (
                    // @ts-expect-error
                    <AccountParentIcon size={this.props.size} color={this.props.color} />
                );
            }
        }

        if (!initials && !avatarImg && !entityIcon) return null;

        return (
            <>
                {avatarImg ? avatarImg : undefined}
                {initials ? initials : entityIcon}
            </>
        );
    }
}
